import React, { useCallback } from 'react'
import { generateClientId } from 'models/Asset/Asset'
import { insertLayer } from 'actions/insertLayer'
import { isArray } from 'lodash'
import { useDispatch } from 'react-redux'
import { pixel2Time } from '~/Util'
import { DraggingItemContext } from '../DraggingItemProvider'
import { DRAGNDROP_TYPE } from '~/enums'
import * as Actions from '~/actions'

export const useLayerDropHandler = () => {
  const { getProps, resetProps } = React.useContext(DraggingItemContext)
  const dispatch = useDispatch()

  return useCallback((item, monitor, {
    scale, layerIndex, layerId,
  }) => {
    if (monitor.didDrop()) {
      resetProps()
      return
    }

    const {
      normalizedSourceX,
      deltaOnDrop,
      isOverItem,
      allowDropOnHover,
      overNewLayerIndex,
      disableDrop,
      dropOffsetX,
      draggableBlockStartTime,
      isIntersectionPlaceholder,
      isInsertBetween,
      intersectionPlaceholder,
      insertBetween,
      isExternalSource,
    } = getProps()

    const isMultipleDrop = isExternalSource
      ? item.type === DRAGNDROP_TYPE.GROUP && item.selectedItems.length > 1
      : item.selectedAssets?.length > 1 && item.selectedAssets.find(asset => asset.id === item.id)

    let action
    let pregeneratedId
    switch (item.type) {
      case DRAGNDROP_TYPE.LAYER_ASSET:
        isMultipleDrop
          ? action = Actions.timeline.moveMultipleAssets
          : action = Actions.timeline.moveAsset
        break
      case DRAGNDROP_TYPE.GROUP:
        action = Actions.timeline.dropSourceFile
        pregeneratedId = Array(item.selectedItems.length).fill(0).map(_ => generateClientId())
        break
      default:
        if (item.type !== DRAGNDROP_TYPE.LAYER
           && item.type !== DRAGNDROP_TYPE.TRANSITION_ITEM
           && item.type !== DRAGNDROP_TYPE.LAYER_TRANSITION_ASSET) {
          action = Actions.timeline.dropSourceFile
          pregeneratedId = generateClientId()
        }
        break
    }

    resetProps()
    dispatch(Actions.timeline.endDragAsset())

    if (disableDrop || !action) {
      return
    }
    if (overNewLayerIndex !== null) {
      if (isExternalSource || !isMultipleDrop) {
        dispatch(insertLayer({ index: overNewLayerIndex }))
      }
      dispatch(action(item.id, {
        startTime: Math.max(0, pixel2Time(normalizedSourceX, scale)),
        layerIndex: overNewLayerIndex,
        pregeneratedId,
        isTransit: true,
        dragItem: item,
        isNewLayerAction: true,
        dropOffsetX: pixel2Time(dropOffsetX, scale),
        isIntersectionPlaceholder: isInsertBetween || isIntersectionPlaceholder,
        intersectionPlaceholder: insertBetween || intersectionPlaceholder,
      }))
    } else if ((isOverItem.size === 1 && allowDropOnHover) || isOverItem.size === 0) {
      dispatch(Actions.layer.shiftItemsByAssetOnDrop({
        id: isArray(pregeneratedId) ? pregeneratedId[0] : pregeneratedId || item.id,
        layerId,
        ignoredAssetsIds: isMultipleDrop && !isExternalSource
          ? item.selectedAssets.map(asset => asset.id)
          : [],
        time: Math.max(0, pixel2Time(normalizedSourceX, scale)),
        delta: pixel2Time(deltaOnDrop, scale),
        isIntersectionPlaceholder: isInsertBetween || isIntersectionPlaceholder,
        intersectionPlaceholder: insertBetween || intersectionPlaceholder,
      }))

      dispatch(action(item.id, {
        startTime: Math.max(0, pixel2Time(normalizedSourceX, scale)),
        layerIndex,
        pregeneratedId,
        deltaOnDrop: pixel2Time(deltaOnDrop, scale),
        dragItem: item,
        isNewLayerAction: false,
        isOverItem,
        dropOffsetX: pixel2Time(dropOffsetX, scale),
        draggableBlockStartTime,
        isIntersectionPlaceholder: isInsertBetween || isIntersectionPlaceholder,
        intersectionPlaceholder: insertBetween || intersectionPlaceholder,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
